// src/pages/notificationstab/NotificationsPage.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { fetchNotifications, markRead, openNotifStream } from "../../NotificationsApi";
import { STREAMERS } from "../../data/streams";
import Topbar from "../../components/topbar/Topbar";
import Leftbar from "../../components/LeftBar/lefbar";

/* ---------- verified helpers ---------- */
const VERIFIED_LOOKUP = (() => {
  const nameSet = new Set();
  const slugSet = new Set();
  for (const [slug, s] of Object.entries(STREAMERS || {})) {
    if (slug) slugSet.add(String(slug).toLowerCase());
    if (s?.displayName) nameSet.add(String(s.displayName).toLowerCase());
    if (s?.name) nameSet.add(String(s.name).toLowerCase());
  }
  return { nameSet, slugSet };
})();

function isActorVerified(n, fallbackName = "") {
  if (!n || (n.type !== "reply" && n.type !== "like")) return false;
  const { nameSet, slugSet } = VERIFIED_LOOKUP;
  const candidatesRaw = [
    n.actorSlug, n.actorUsername, n.actorDisplayName, n.actorName,
    n.who, fallbackName, n?.actor?.slug, n?.actor?.username,
    n?.actor?.displayName, n?.actor?.name,
  ].filter(Boolean);

  const norm = (v) => String(v).trim().replace(/^@/, "").toLowerCase();
  for (const c of candidatesRaw) {
    const v = norm(c);
    if (slugSet.has(v) || nameSet.has(v)) return true;
  }
  return false;
}

/* tiny verified icon (twitter-like) */
function VerifiedCheck({ size = 12, ml = 6 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true"
      style={{ display: "inline-block", marginLeft: ml, verticalAlign: "middle", flex: "0 0 auto" }}>
      <circle cx="12" cy="12" r="12" fill="#1DA1F2" />
      <path d="M17 9l-6.2 6L7 11.5" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* back icon */
function ArrowLeftIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

/* ---------- tiny utils ---------- */
function copyFor(n) {
  switch (n.type) {
    case "live":   return "is live now";
    case "replay": return "posted a replay";
    case "reply":  return "replied to your comment";
    case "like":   return "liked your comment";
    default:       return "activity";
  }
}
function fmtTime(ts) {
  const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24); return `${d}d`;
}
function NotifIcon({ type }) {
  const size = 20;
  const common = { width: size, height: size, stroke: "currentColor", strokeWidth: 2, fill: "none" };
  if (type === "live")   return <svg {...common} viewBox="0 0 24 24"><circle cx="12" cy="12" r="4"/><path d="M2 12a10 10 0 0 1 20 0"/></svg>;
  if (type === "replay") return <svg {...common} viewBox="0 0 24 24"><path d="M3 12a9 9 0 1 0 9-9"/><polyline points="3 3 3 9 9 9"/></svg>;
  if (type === "reply")  return <svg {...common} viewBox="0 0 24 24"><path d="M10 19l-7-7 7-7"/><path d="M4 12h11a5 5 0 1 1 0 10H9"/></svg>;
  return <svg {...common} viewBox="0 0 24 24"><path d="M20.8 8a5.4 5.4 0 0 0-9.6-3.6L11 5l-.2-.6A5.4 5.4 0 1 0 3.2 14L11 21l7.8-7a5.4 5.4 0 0 0 2-6z"/></svg>;
}

/* ---------- styles (Twitter-ish, mobile-first) ---------- */
const wrap = { background: "#fff", minHeight: "100vh" };

/* mobile header (hidden on desktop) */
const head = {
  position: "sticky", top: 0, zIndex: 10, background: "#fff",
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "12px 14px", borderBottom: "1px solid #eee"
};
const title = { margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: 0.2 };
const badge = { background: "#111", color: "#fff", borderRadius: 999, padding: "2px 8px", fontSize: 12, fontWeight: 700 };

/* tabs row (sticky) */
const tabsRowBase = {
  display: "flex", gap: 8, padding: "8px 12px",
  borderBottom: "1px solid #eee", position: "sticky",
  background: "#fff", zIndex: 9
};
const tabBtn = {
  border: "1px solid #e6e6e6", background: "#fafafa", color: "#111",
  borderRadius: 999, padding: "6px 10px", fontSize: 13, fontWeight: 600
};
const tabBtnActive = { ...tabBtn, background: "#111", color: "#fff", borderColor: "#111" };

const loading = { padding: 12, color: "#666" };
const errorBox = { padding: 12, color: "#b91c1c", fontSize: 13 };
const emptyBox = { padding: 12, color: "#666" };

const list = { listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 1 };

const row = { borderBottom: "1px solid #f0f0f0" };
const rowBtn = {
  width: "100%", display: "grid",
  gridTemplateColumns: "44px 1fr 28px",
  gap: 10, alignItems: "center",
  padding: "10px 14px", background: "transparent",
  border: "none", textAlign: "left", cursor: "pointer"
};

const sideWrap = { display: "grid", placeItems: "center" };
const iconCircleUnread = (read) => ({
  width: 36, height: 36, borderRadius: 18,
  display: "grid", placeItems: "center",
  border: read ? "1px solid #eee" : "1px solid #111",
  color: read ? "#555" : "#111", background: read ? "#fff" : "#fff"
});

const midWrap = { minWidth: 0 };
const topLine = (read) => ({ fontSize: 14, lineHeight: 1.25, color: "#111", fontWeight: read ? 500 : 800 });
const whoStyle = (read) => ({ fontWeight: read ? 600 : 800 });
const copyStyle = (read) => ({ color: "#111", opacity: read ? 0.85 : 1 });

const preview = { marginTop: 4, fontSize: 13, color: "#333" };

const metaRow = { display: "flex", alignItems: "center", gap: 10, marginTop: 6 };
const avatarWrap = {
  width: 28, height: 28, borderRadius: "50%", overflow: "hidden",
  background: "#eee", display: "grid", placeItems: "center"
};
const avatarImg = { width: "100%", height: "100%", objectFit: "cover" };

const timeAgoStyle = { fontSize: 12, color: "#666" };
const rightWrap = { display: "grid", placeItems: "center" };
const dot = { width: 8, height: 8, background: "#111", borderRadius: "50%" };

/* desktop topbar (white/black) */
const DESK_TOPBAR_CSS = `
@media (min-width:1024px){
  .desk-topbar {
    position: sticky;
    top: 56px;                /* sits under the global Topbar */
    z-index: 60;
    height: 56px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 14px;
    background: #fff;
    border-bottom: 1px solid #eee;
  }
  .desk-back {
    appearance: none;
    border: none;
    outline: none;
    padding: 8px;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    color: #111;
    background: transparent;
    cursor: pointer;
    transition: background .15s ease, transform .06s ease;
  }
  .desk-back:hover { background: #f2f2f2; }
  .desk-back:active { transform: translateY(1px) scale(0.98); }
  .desk-title {
    color: #111;
    font-weight: 800;
    letter-spacing: 0.02em;
    font-size: 15px;
    text-transform: uppercase;
    line-height: 1;
  }
  .mobile-head { display: none !important; }
}
`;

/* desktop shell layout: show left bar */
const DESK_LAYOUT_CSS = `
@media (min-width:1024px){
  .desk-shell {
    display: grid;
    grid-template-columns: 256px 1fr; /* leftbar width + content */
    min-height: 100vh;
    background: #fff;
  }
  .desk-left {
    border-right: 1px solid #eee;
    background: #fff;
    position: sticky;
    top: 0;
    height: 100vh;
    overflow: auto; /* scroll if left list is long */
  }
  .desk-main {
    min-width: 0; /* allow content to shrink */
  }
}
`;

/* desktop global Topbar wrapper (white/black, sticky) */
const DESK_TOP_NAV_CSS = `
@media (min-width:1024px){
  .desk-global-topbar {
    position: sticky;
    top: 0;
    z-index: 70;              /* above desk-topbar */
    background: #fff;         /* always white */
    border-bottom: 1px solid #eee;
  }
}
`;

export default function NotificationsPage() {
  const { user } = useContext(AuthContext);
  const { pathname } = useLocation(); // eslint-disable-line no-unused-vars
  const nav = useNavigate();

  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.matchMedia("(min-width:1024px)").matches
  );
  useEffect(() => {
    const mm = window.matchMedia("(min-width:1024px)");
    const onChange = () => setIsDesktop(mm.matches);
    mm.addEventListener?.("change", onChange);
    return () => mm.removeEventListener?.("change", onChange);
  }, []);

  const [items, setItems] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error | anon
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("all"); // all | unread

  const unreadCount = useMemo(() => items.filter(i => !i.read).length, [items]);
  const shown = useMemo(
    () => tab === "unread" ? items.filter(i => !i.read) : items,
    [items, tab]
  );

  useEffect(() => {
    try {
      localStorage.setItem("yzy_unread_count", String(unreadCount));
      window.dispatchEvent(new Event("yzy_unread_update"));
    } catch {}
  }, [unreadCount]);

  useEffect(() => {
    let alive = true;
    let es;
    let timerId;

    if (!user) {
      setItems([]);
      setState("anon");
      try {
        localStorage.setItem("yzy_unread_count", "0");
        window.dispatchEvent(new Event("yzy_unread_update"));
      } catch {}
      return () => {};
    }

    const load = async () => {
      if (!alive) return;
      setState(s => (s === "ready" ? "ready" : "loading"));
      setErr("");
      try {
        const data = await fetchNotifications({ limit: 50 });
        const list = Array.isArray(data?.items) ? data.items : [];
        if (!alive) return;
        setItems(list);
        setState("ready");
      } catch (e) {
        if (!alive) return;
        setItems([]);
        setErr(String(e?.message || e));
        setState("error");
      }
    };

    load();

    try {
      es = openNotifStream();
      es.onmessage = (evt) => {
        if (!alive) return;
        try {
          const msg = JSON.parse(evt.data || "{}");
          if (Array.isArray(msg.items)) setItems(msg.items);
          const one = msg.item || msg.notification;
          if (one) setItems(prev => [one, ...prev]);
          if (typeof msg.unreadCount === "number") {
            localStorage.setItem("yzy_unread_count", String(msg.unreadCount));
            window.dispatchEvent(new Event("yzy_unread_update"));
          }
        } catch {}
      };
      es.onerror = () => {};
    } catch {}

    timerId = setInterval(load, 30000);

    return () => {
      alive = false;
      if (es) es.close();
      clearInterval(timerId);
    };
  }, [user]);

  const setOneRead = async (id, read) => {
    setItems(prev => prev.map(i => (i._id === id || i.id === id) ? { ...i, read } : i));
    try { await markRead({ ids: [id], read }); } catch {}
  };

  const openNotification = async (n) => {
    const id = n._id || n.id;
    const url = n.url || (n.clipKey ? `/${String(n.clipKey).replace(":", "/")}` : "/");
    if (!n.read) setOneRead(id, true);
    nav(url);
  };

  const goBackToForYou = () => {
    const state = window.history.state;
    if (state && window.history.length > 1) return nav(-1);
    nav("/", { replace: true });
  };

  // Both bars (Topbar + page back bar) are 56px each on desktop => 112 sticky offset
  const tabsRow = { ...tabsRowBase, top: isDesktop ? 112 : 48 };

  return (
    <div style={wrap}>
      <style>{DESK_TOPBAR_CSS + DESK_LAYOUT_CSS + DESK_TOP_NAV_CSS}</style>

      {/* Desktop shell with Topbar + Leftbar */}
      {isDesktop ? (
        <div className="desk-shell">
          <aside className="desk-left">
            <Leftbar />
          </aside>

          <main className="desk-main">
            {/* Global Topbar (desktop only) */}
            <div className="desk-global-topbar">
              <Topbar />
            </div>

            {/* Page-level back bar (desktop only) */}
            <header className="desk-topbar">
              <button type="button" className="desk-back" aria-label="Back" onClick={goBackToForYou}>
                <ArrowLeftIcon size={20} />
              </button>
              <div className="desk-title">Notifications</div>
            </header>

            {/* Tabs */}
            <div style={tabsRow}>
              <button
                onClick={() => setTab("all")}
                style={tab === "all" ? tabBtnActive : tabBtn}
                aria-current={tab === "all" ? "page" : undefined}
              >
                All
              </button>
              <button
                onClick={() => setTab("unread")}
                style={tab === "unread" ? tabBtnActive : tabBtn}
                aria-current={tab === "unread" ? "page" : undefined}
              >
                Unread{unreadCount ? ` (${unreadCount})` : ""}
              </button>
            </div>

            {state === "loading" && <div style={loading}>Loading…</div>}
            {state === "error" && (
              <div style={errorBox}>Couldn’t load notifications from server ({err}).</div>
            )}
            {state === "ready" && shown.length === 0 && (
              <div style={emptyBox}>{tab === "unread" ? "No unread notifications." : "No notifications yet."}</div>
            )}

            <ul style={list}>
              {shown.map((n) => {
                const id   = n._id || n.id;
                const read = !!n.read;
                const type = n.type || "reply";
                const who  = n.actorDisplayName || n.actorName || n.who || "Someone";
                const ts   = n.createdAt ? Date.parse(n.createdAt) : (n.ts || Date.now());
                const avatar =
                  n.actorAvatar || n.avatar || n.profilePicture || n.actorProfilePicture ||
                  (n.actor && n.actor.profilePicture) || (n.user && n.user.profilePicture) || "";
                const showCheck = isActorVerified(n, who);

                return (
                  <li key={id} style={{...row, background: read ? "#fff" : "#f7f9fb"}}>
                    <button onClick={() => openNotification(n)} style={rowBtn} aria-label={`${who} ${copyFor(n)}`}>
                      <div style={sideWrap}>
                        <div style={iconCircleUnread(read)}><NotifIcon type={type}/></div>
                      </div>
                      <div style={midWrap}>
                        <div style={topLine(read)}>
                          <strong style={whoStyle(read)}>{who}</strong>
                          {showCheck && <VerifiedCheck size={12} ml={6} />}
                          <span style={copyStyle(read)}>&nbsp;{copyFor(n)}</span>
                        </div>
                        {n.preview && <div style={preview}>{n.preview}</div>}
                        <div style={metaRow}>
                          {avatar && <div style={avatarWrap}><img src={avatar} alt="" style={avatarImg} /></div>}
                          <span style={timeAgoStyle}>{fmtTime(ts)}</span>
                        </div>
                      </div>
                      <div style={rightWrap}>{!read && <span style={dot} aria-hidden="true" />}</div>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div style={{ height: 72 }} />
          </main>
        </div>
      ) : (
        /* Mobile layout (no left bar, no desktop Topbar/backbar) */
        <>
          <header className="mobile-head" style={head}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <h1 style={title}>NOTIFICATIONS</h1>
              {unreadCount > 0 && <span style={badge}>{unreadCount}</span>}
            </div>
          </header>

          {/* Tabs */}
          <div style={tabsRow}>
            <button
              onClick={() => setTab("all")}
              style={tab === "all" ? tabBtnActive : tabBtn}
              aria-current={tab === "all" ? "page" : undefined}
            >
              All
            </button>
            <button
              onClick={() => setTab("unread")}
              style={tab === "unread" ? tabBtnActive : tabBtn}
              aria-current={tab === "unread" ? "page" : undefined}
            >
              Unread{unreadCount ? ` (${unreadCount})` : ""}
            </button>
          </div>

          {state === "loading" && <div style={loading}>Loading…</div>}
          {state === "error" && (
            <div style={errorBox}>Couldn’t load notifications from server ({err}).</div>
          )}
          {state === "ready" && shown.length === 0 && (
            <div style={emptyBox}>{tab === "unread" ? "No unread notifications." : "No notifications yet."}</div>
          )}

          <ul style={list}>
            {shown.map((n) => {
              const id   = n._id || n.id;
              const read = !!n.read;
              const type = n.type || "reply";
              const who  = n.actorDisplayName || n.actorName || n.who || "Someone";
              const ts   = n.createdAt ? Date.parse(n.createdAt) : (n.ts || Date.now());
              const avatar =
                n.actorAvatar || n.avatar || n.profilePicture || n.actorProfilePicture ||
                (n.actor && n.actor.profilePicture) || (n.user && n.user.profilePicture) || "";
              const showCheck = isActorVerified(n, who);

              return (
                <li key={id} style={{...row, background: read ? "#fff" : "#f7f9fb"}}>
                  <button onClick={() => openNotification(n)} style={rowBtn} aria-label={`${who} ${copyFor(n)}`}>
                    <div style={sideWrap}>
                      <div style={iconCircleUnread(read)}><NotifIcon type={type}/></div>
                    </div>
                    <div style={midWrap}>
                      <div style={topLine(read)}>
                        <strong style={whoStyle(read)}>{who}</strong>
                        {showCheck && <VerifiedCheck size={12} ml={6} />}
                        <span style={copyStyle(read)}>&nbsp;{copyFor(n)}</span>
                      </div>
                      {n.preview && <div style={preview}>{n.preview}</div>}
                      <div style={metaRow}>
                        {avatar && <div style={avatarWrap}><img src={avatar} alt="" style={avatarImg} /></div>}
                        <span style={timeAgoStyle}>{fmtTime(ts)}</span>
                      </div>
                    </div>
                    <div style={rightWrap}>{!read && <span style={dot} aria-hidden="true" />}</div>
                  </button>
                </li>
              );
            })}
          </ul>

          <div style={{ height: 72 }} />
        </>
      )}
    </div>
  );
}
